import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, Package, TrendingUp, Truck,
  Mail, Bell, Settings, LogOut, ChevronLeft, ChevronRight, Lock, BarChart3, PieChart, ShoppingCart
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { ROUTES, POST_LOGOUT_REDIRECT_KEY } from '../../lib/constants'
import { isDemoMode } from '../../utils/demo'
import styles from './Sidebar.module.css'
import Logo from '../ui/Logo'
import logoIcon from '../../assets/logo-icon.png'

const NAV_ITEMS = [
  { label: 'Dashboard',        icon: LayoutDashboard, path: ROUTES.DASHBOARD,        blocked: true  },
  { label: 'Products',         icon: Package,          path: ROUTES.PRODUCTS,         blocked: true  },
  { label: 'Predictions',      icon: TrendingUp,       path: ROUTES.PREDICTIONS,      blocked: true  },
  { label: 'ABC Analysis',     icon: PieChart,         path: ROUTES.ABC_ANALYSIS,     blocked: true  },
  { label: 'Purchase Orders',  icon: ShoppingCart,     path: ROUTES.PURCHASE_ORDERS,  blocked: true  },
  { label: 'Reports',          icon: BarChart3,        path: ROUTES.REPORTS,          blocked: true  },
  { label: 'Suppliers',        icon: Truck,            path: ROUTES.SUPPLIERS,        blocked: true  },
  { label: 'Email Drafts',     icon: Mail,             path: ROUTES.EMAIL_DRAFTS,     blocked: true  },
  { label: 'Alerts',           icon: Bell,             path: ROUTES.ALERTS,           blocked: false },
  { label: 'Settings',         icon: Settings,         path: ROUTES.SETTINGS,         blocked: false },
]

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const { user, profile, signOut } = useAuth()
  const toast = useToast()

  const isExpired = (() => {
    if (!profile) return false
    const { subscription_status, trial_ends_at } = profile
    if (subscription_status === 'active') return false
    if (subscription_status === 'expired' || subscription_status === 'cancelled') return true
    if (subscription_status === 'trialing') {
      if (!trial_ends_at) return false
      return new Date() > new Date(trial_ends_at)
    }
    return false
  })()

  async function handleSignOut() {
    // ProtectedRoute is what actually redirects away once `user` goes null
    // after signOut() resolves — calling navigate() here ourselves would
    // just race it. Leave it a flag so it sends demo users to the homepage
    // instead of the (unlinked) login page.
    if (isDemoMode(user?.id)) {
      sessionStorage.setItem(POST_LOGOUT_REDIRECT_KEY, '/')
    }
    try {
      await signOut()
      toast.success('Signed out successfully')
    } catch {
      toast.error('Failed to sign out')
    }
  }

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User'
  const displayEmail = user?.email || ''

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      {/* Logo */}
      <div className={styles.logo}>
        {collapsed
          ? <Logo variant="icon" size="sm" />
          : (
            <div className={styles.logoBlock}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 1, lineHeight: 1 }}>
                <Logo variant="icon" size="sm" />
                <span className={styles.logoText}>Debnix</span>
              </div>
              <div className={styles.syncStatus}>
                <span className={`${styles.syncDot} ${profile?.is_shopify_connected ? styles.syncDotGreen : styles.syncDotGray}`} />
                <span className={styles.syncLabel}>
                  {profile?.is_shopify_connected ? 'Connected' : 'Not connected'}
                </span>
              </div>
            </div>
          )
        }
      </div>

      {/* Nav */}
      <nav className={styles.nav}>
        {NAV_ITEMS.map(({ label, icon: Icon, path, blocked }) => {
          const isLocked = isExpired && blocked
          return (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''} ${isLocked ? styles.navItemLocked : ''}`
              }
              title={collapsed ? label : undefined}
            >
              <Icon size={18} className={styles.navIcon} />
              {!collapsed && <span className={styles.navLabel}>{label}</span>}
              {!collapsed && isLocked && (
                <Lock size={11} className={styles.lockIcon} />
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Bottom */}
      <div className={styles.bottom}>
        <button className={styles.collapseBtn} onClick={() => setCollapsed(p => !p)}>
          {collapsed
            ? <ChevronRight size={16} />
            : <><ChevronLeft size={16} /><span>Collapse</span></>
          }
        </button>

        <div className={styles.userInfo} title={collapsed ? displayEmail : undefined}>
          <div className={styles.avatar}>
            <img src={logoIcon} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 2 }} />
          </div>
          {!collapsed && (
            <div className={styles.userText}>
              <span className={styles.userName}>{displayName}</span>
              <span className={styles.userEmail}>{displayEmail}</span>
            </div>
          )}
          {!collapsed && (
            <button className={styles.logoutBtn} onClick={handleSignOut} title="Sign out">
              <LogOut size={15} />
            </button>
          )}
        </div>

        {collapsed && (
          <button className={styles.logoutBtnCollapsed} onClick={handleSignOut} title="Sign out">
            <LogOut size={16} />
          </button>
        )}
      </div>
    </aside>
  )
}